import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import Button from "./ToolbarButton";
import { ToolbarWrap, Separator } from "./ToolbarUtils";
import { useEditorContext } from "./EditorContext";
import { useEditor } from "prosekit/react";
import { fetchApi } from "../../../helpers/reactQueryApi";
import { jsonFromHTML } from "prosekit/core";
import { DOMSerializer } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import type { EditorExtension } from "./extension";
import AdaptiveIcon from "@/components/shared/AdaptiveIcon.tsx";
import { aiRequestErrorMessage } from "../aiRequestError";

interface Prompt {
  id: string;
  title: string;
}

interface AiToolbarProps {
  onBack: () => void;
}

const VISIBLE_PROMPT_COUNT = 6;

// Prompt titles use the full button width and are cut with an ellipsis instead
// of wrapping (#305). On hover the button shows the complete title (wrapping
// over several lines); the native title tooltip carries it as well.
function PromptButton({
  prompt,
  processing,
  disabled,
  onSelect,
}: {
  prompt: Prompt;
  processing: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      title={prompt.title}
      onClick={onSelect}
      disabled={disabled}
      className="btn btn-light btn-sm w-full min-w-0 flex items-center text-left group"
    >
      {processing ? (
        <FontAwesomeIcon icon={faSpinner} className="mr-1" spin />
      ) : null}
      <span className="truncate group-hover:whitespace-normal group-hover:overflow-visible group-hover:text-clip">
        {prompt.title}
      </span>
    </button>
  );
}

export default function AiToolbar({ onBack }: AiToolbarProps) {
  const editor = useEditor<EditorExtension>();
  const { readOnly } = useEditorContext();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [processingPromptId, setProcessingPromptId] = useState<string | null>(
    null,
  );
  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPrompts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchApi("/ai/prompts");
        if (!cancelled) {
          setPrompts(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(I18n.t("js.composer.editor.ai.fetch_error"));
          console.error("Error fetching prompts:", err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPrompts();

    return () => {
      cancelled = true;
    };
  }, []);

  // Helper function to get HTML of selected content
  const getSelectedHTML = (from: number, to: number): string => {
    const fragment = editor.state.doc.slice(from, to).content;
    const serializer = DOMSerializer.fromSchema(editor.schema);
    const domFragment = serializer.serializeFragment(fragment);

    const temp = document.createElement("div");
    temp.appendChild(domFragment);
    return temp.innerHTML;
  };

  // Poll for AI request result with progressive updates
  const pollForResult = async (
    jobId: string,
    updateEditor?: (html: string) => void,
  ): Promise<string> => {
    const maxAttempts = 120; // 2 minutes max
    const pollInterval = 500; // 500ms for smoother updates during streaming

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await fetchApi(`/ai/llm_requests/${jobId}`);

      if (result.status === "completed") {
        return result.response;
      } else if (result.status === "failed") {
        // Show a generic, localized message — never surface the raw backend/provider
        // error (e.g. "IONOS service error: 502") to the user.
        throw new Error(
          I18n.t("js.composer.editor.ai.service_unavailable", {
            defaultValue: I18n.t("js.composer.editor.ai.request_error"),
          }),
        );
      }

      // Update editor with partial text during streaming
      if (
        result.response &&
        (result.status === "streaming" || result.status === "processing") &&
        updateEditor
      ) {
        updateEditor(result.response);
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    throw new Error(I18n.t("js.composer.editor.ai.timeout_error"));
  };

  const handlePromptSelect = async (promptId: string) => {
    // Save editor state before any AI modifications so we can restore it for undo
    let savedState: EditorState | null = null;

    try {
      setProcessingPromptId(promptId);
      setError(null);

      // Get the selected text, or full document if nothing selected
      const { from, to, empty } = editor.state.selection;

      // Get the content as HTML
      const html = empty ? editor.getDocHTML() : getSelectedHTML(from, to);

      if (!html.trim()) {
        setError(I18n.t("js.composer.editor.ai.empty_text"));
        setProcessingPromptId(null);
        return;
      }

      // Snapshot the state before any editor modifications
      savedState = editor.state;

      // Start async AI request
      const startResponse = await fetchApi("/ai/llm_requests", {
        method: "POST",
        body: {
          prompt_id: promptId,
          llm_request: { text: html },
        },
      });

      if (!startResponse.job_id) {
        setError(
          startResponse.error || I18n.t("js.composer.editor.ai.request_error"),
        );
        setProcessingPromptId(null);
        return;
      }

      // For partial selection: delete selection first and track insert position
      const insertPos = from;
      let lastInsertedSize = 0;

      if (!empty) {
        // Delete the selection before streaming starts
        const tr = editor.state.tr.deleteRange(from, to);
        editor.updateState(editor.state.apply(tr));
        lastInsertedSize = 0;
      }

      // Create update function for progressive streaming display
      const updateEditorContent = (partialHtml: string) => {
        const docJson = jsonFromHTML(partialHtml, { schema: editor.schema });
        const doc = editor.schema.nodeFromJSON(docJson);

        // Extract inline content if wrapped in single paragraph
        let content = doc.content;
        if (
          doc.content.childCount === 1 &&
          doc.content.firstChild?.type.name === "paragraph"
        ) {
          content = doc.content.firstChild.content;
        }

        if (empty) {
          // Full document: just set the content
          editor.setContent(docJson);
        } else {
          // Partial selection: replace previously inserted content with new content
          const tr = editor.state.tr.replaceWith(
            insertPos,
            insertPos + lastInsertedSize,
            content,
          );
          editor.updateState(editor.state.apply(tr));
          lastInsertedSize = content.size;
        }
      };

      // Poll for result with progressive updates
      const responseHtml = await pollForResult(
        startResponse.job_id,
        updateEditorContent,
      );

      if (responseHtml) {
        // Final update: restore saved state and dispatch through history
        const docJson = jsonFromHTML(responseHtml, { schema: editor.schema });
        const doc = editor.schema.nodeFromJSON(docJson);

        // Restore the pre-AI state so the history plugin sees the original content
        editor.updateState(savedState);

        if (empty) {
          // Full document: replace entire document content
          const tr = savedState.tr.replaceWith(
            0,
            savedState.doc.content.size,
            doc.content,
          );
          editor.view.dispatch(tr);
        } else {
          // Partial selection: replace the originally selected range
          let content = doc.content;
          if (
            doc.content.childCount === 1 &&
            doc.content.firstChild?.type.name === "paragraph"
          ) {
            content = doc.content.firstChild.content;
          }

          const tr = savedState.tr.replaceWith(from, to, content);
          editor.view.dispatch(tr);
        }

        onBack(); // Return to main toolbar after successful replacement
      } else {
        setError(I18n.t("js.composer.editor.ai.response_error"));
      }
    } catch (err) {
      console.error("Error processing AI request:", err);

      // Restore original content on error
      if (savedState) {
        editor.updateState(savedState);
      }

      setError(aiRequestErrorMessage(err));
    } finally {
      setProcessingPromptId(null);
    }
  };

  return (
    <ToolbarWrap>
      <Button
        onClick={onBack}
        tooltip={I18n.t("js.composer.editor.buttons.back_to_toolbar")}
        disabled={readOnly || loading || processingPromptId !== null}
      >
        <AdaptiveIcon icon={faChevronLeft} />
      </Button>
      <Separator />
      {loading ? (
        <div className="p-2 flex items-center">
          <FontAwesomeIcon icon={faSpinner} className="mr-2" spin />
          <span>{I18n.t("js.composer.editor.ai.loading")}</span>
        </div>
      ) : error ? (
        <div className="p-2 text-red-600">{error}</div>
      ) : prompts.length === 0 ? (
        <div className="p-2">{I18n.t("js.composer.editor.ai.no_prompts")}</div>
      ) : (
        (() => {
          const hasOverflow = prompts.length > VISIBLE_PROMPT_COUNT;
          const visiblePrompts =
            expanded || !hasOverflow
              ? prompts
              : prompts.slice(0, VISIBLE_PROMPT_COUNT);
          const overflowCount = prompts.length - VISIBLE_PROMPT_COUNT;
          return (
            <div
              className={`grid grid-cols-2 gap-1 p-2 grow${
                expanded ? " max-h-40 overflow-y-auto" : ""
              }`}
            >
              {visiblePrompts.map((prompt) => (
                <PromptButton
                  key={prompt.id}
                  prompt={prompt}
                  processing={processingPromptId === prompt.id}
                  disabled={readOnly || processingPromptId !== null}
                  onSelect={() => handlePromptSelect(prompt.id)}
                />
              ))}
              {hasOverflow && !expanded && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  disabled={readOnly || processingPromptId !== null}
                  className="btn btn-light btn-sm w-full text-left col-span-2 italic"
                >
                  {I18n.t("js.composer.editor.ai.show_more", {
                    count: overflowCount,
                  })}
                </button>
              )}
            </div>
          );
        })()
      )}
    </ToolbarWrap>
  );
}
